import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {savedViewsApi, type SavedViewPage} from './savedViews';

const savedViewsKey = (page: SavedViewPage) => ['me', 'saved-views', page] as const;

export const useSavedViews = (page: SavedViewPage) =>
  useQuery({
    queryKey: savedViewsKey(page),
    queryFn: async () => savedViewsApi.list(page),
    staleTime: 5 * 60_000
  });

// Each write refetches the page's list rather than patching it: the server orders by name and
// decides a name clash, and a list of a few views is cheap to reload.
export const useCreateSavedView = (page: SavedViewPage) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({name, query}: {name: string; query: object}) =>
      savedViewsApi.create(page, name, query),
    onSettled: async () => qc.invalidateQueries({queryKey: savedViewsKey(page)})
  });
};

export const useUpdateSavedView = (page: SavedViewPage) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({id, ...change}: {id: string; name?: string; query?: object}) =>
      savedViewsApi.update(id, change),
    onSettled: async () => qc.invalidateQueries({queryKey: savedViewsKey(page)})
  });
};

export const useDeleteSavedView = (page: SavedViewPage) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => savedViewsApi.remove(id),
    onSettled: async () => qc.invalidateQueries({queryKey: savedViewsKey(page)})
  });
};
